import { Languages } from 'lucide-react';
import { useI18n } from '../i18n';
import { LOCALE_LABELS } from '../i18n/locales';
import { MACHINE_TRANSLATED_COMPLIANCE_LOCALES } from '../i18n/complianceTranslation';

/**
 * Tells the reader, in their own language, that the legal and policy wording
 * they are reading — the full legal documents included — is MACHINE-TRANSLATED
 * and not yet checked by a native speaker, and that some messages are still
 * English.
 *
 * HISTORY (read before rewording). Until 2026-10-19 this was `EnglishCopyNote`,
 * and it explained why compliance wording was held in English: "we do not
 * machine-translate them. They appear in {language} only once a native speaker
 * has checked each one." On 2026-10-19 the founder decided, in writing, that the
 * held-English sentences should be machine-translated. From that moment the old
 * sentence was UNTRUE, so it went with the decision rather than after it.
 *
 * ON THE WORDING — it states a STATUS that the repo can prove, and a POLICY.
 *  - Status: "machine-translated and has not yet been checked by a native
 *    speaker" is exactly what `machine-translated-compliance.json` records. It
 *    says "most", not "all", because Hindi and Bengali also carry an older
 *    hand-curated seed. Since the second founder decision of 2026-10-19 ("yes do
 *    all the documents the same way") the legal DOCUMENTS are machine-translated
 *    too, so the old clause "some is still shown only in English, including the
 *    full legal documents" became untrue and went with that change. "Some
 *    messages are still shown only in English" stays true on every client: server
 *    error messages, notes assembled from the reader's own figures (a templated
 *    sentence has no catalog key) and the founder console are still English.
 *    If the day comes when nothing is English, delete that sentence — do not
 *    leave it standing as a hedge.
 *  - Policy: "the English wording is what Sansara means" if the two differ. That
 *    is a statement of which text the company stands behind, not a claim that
 *    anything has been reviewed. If counsel wants different precedence wording,
 *    change the English key and re-translate.
 * Never let this drift into a claim that a review is underway or done — Conv-31b
 * shipped exactly that untruth once, faithfully translated nine times.
 *
 * Renders NOTHING for English, and nothing for a locale removed from
 * MACHINE_TRANSLATED_COMPLIANCE_LOCALES (i.e. natively checked end to end).
 */
export function MachineTranslationNote({ className = '' }: { className?: string }) {
  const { locale, t } = useI18n();
  if (!MACHINE_TRANSLATED_COMPLIANCE_LOCALES.includes(locale)) return null;
  const language = LOCALE_LABELS[locale] ?? locale;
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border border-line bg-surface-sunken px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60 ${className}`}
    >
      <Languages size={16} className="mt-0.5 shrink-0 text-ink-soft dark:text-slate-400" aria-hidden="true" />
      <div>
        <p className="text-sm font-semibold">{t('About the translation of this wording')}</p>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft dark:text-slate-400">
          {t('Most legal and policy wording in {language}, including the full legal documents, is machine-translated and has not yet been checked by a native speaker. Some messages are still shown only in English. If the {language} and the English ever read differently, the English wording is what Sansara means.', { language })}
        </p>
      </div>
    </div>
  );
}
